import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNode, useEditor } from "@craftjs/core";
import {
    FormControl,
    FormLabel,
    TextField,
    Box,
    Typography,
    Alert,
    Paper,
    Divider,
    Switch,
    FormControlLabel,
    Select,
    MenuItem,
    Button,
    Tabs,
    Tab,
    IconButton
} from "@mui/material";
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    Home as AddressIcon,
    Person as PersonIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Assignment as FormIcon,
    List as RequestsIcon,
    Close as CloseIcon
} from "@mui/icons-material";

import ViewSwitcher from '../../../Stack/views/ViewSwitcher';
import { getSelectionStyle } from '../../../Stack/utils/stackUtils';
import { newQwanta } from '../../../../qwanyx-core/newQwanta';
import { persist } from '../../../../qwanyx-core/api-client';
import { deleteInstance } from '../../../../qwanyx-core/deleteInstance';

// Générer un UUID simple pour la session
const generateSessionId = () => {
    return 'session-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36);
};

// Context pour partager les données utilisateur avec les templates
const RequestContext = createContext();

// Hook pour accéder au contexte de création de demandes
export const useRequestContext = () => {
    const context = useContext(RequestContext);
    if (!context) {
        throw new Error('useRequestContext must be used within a RequestProvider');
    }
    return context;
};

// Composant formulaire utilisateur
const UserInfoForm = ({ 
    userInfo, 
    onUserInfoChange, 
    isValid,
    title = "Vos coordonnées",
    emailRequired = true,
    showPhone = true,
    showAddress = true 
}) => {
    const [errors, setErrors] = useState({});

    const validateField = (field, value) => {
        const newErrors = { ...errors };
        
        if (field === 'email') {
            if (!value || !value.trim()) {
                newErrors.email = 'Email requis';
            } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                newErrors.email = 'Format email invalide';
            } else {
                delete newErrors.email;
            }
        }
        
        if (field === 'nom') {
            if (!value || !value.trim()) {
                newErrors.nom = 'Nom requis';
            } else {
                delete newErrors.nom;
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFieldChange = (field, value) => {
        onUserInfoChange({ ...userInfo, [field]: value });
        validateField(field, value);
    };

    return (
        <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <PersonIcon color="primary" />
                <Typography variant="h6" component="h2">
                    {title}
                </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="Email"
                    type="email"
                    value={userInfo.email || ''}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    required={emailRequired}
                    error={!!errors.email}
                    helperText={errors.email}
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: <EmailIcon sx={{ mr: 1, color: '#666' }} />
                    }}
                />
                
                <TextField
                    label="Nom"
                    value={userInfo.nom || ''}
                    onChange={(e) => handleFieldChange('nom', e.target.value)}
                    required
                    error={!!errors.nom}
                    helperText={errors.nom}
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: <PersonIcon sx={{ mr: 1, color: '#666' }} />
                    }}
                />
                
                {showPhone && (
                    <TextField
                        label="Téléphone"
                        type="tel"
                        value={userInfo.telephone || ''}
                        onChange={(e) => handleFieldChange('telephone', e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <PhoneIcon sx={{ mr: 1, color: '#666' }} />
                        }}
                    />
                )}
                
                {showAddress && (
                    <TextField
                        label="Adresse"
                        multiline
                        rows={2}
                        value={userInfo.adresse || ''}
                        onChange={(e) => handleFieldChange('adresse', e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                            startAdornment: <AddressIcon sx={{ mr: 1, color: '#666', alignSelf: 'flex-start', mt: 1 }} />
                        }}
                    />
                )}
            </Box>
            
            {!isValid && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Veuillez remplir tous les champs obligatoires pour créer des demandes.
                </Alert>
            )}
        </Paper>
    );
};

export const Requests = ({
    name = "Requests",
    
    // Configuration de base - Simple comme Shop
    requestDomain = "requests",
    requestTemplate = null, // Template unique pour afficher les demandes
    
    // Configuration utilisateur
    requireUserInfo = true,
    userFormTitle = "Vos coordonnées",
    emailRequired = true,
    showPhone = true,
    showAddress = true,
    
    // ViewSwitcher props (identiques au Stack)
    showSearch = true,
    showLoadingState = true,
    searchPlaceholder = "Rechercher...",
    searchFields = ['title', 'name', 'description'],
    facetFields = [],
    facetConfig = [],
    itemTemplate = null,
    categoryTemplate = null,
    useCompact = true,
    templateProjection = [],
    
    // Tri et ordre
    sortBy = 'requestDate', // Champ de tri par défaut
    sortOrder = 'desc', // Ordre par défaut (desc = plus récent d'abord)
    showSortControls = false, // Masquer les contrôles de tri pour l'utilisateur
    
    // Styling
    width = "100%",
    height = "600px",
    itemHeight = 120, // Hauteur fixe de chaque item en pixels
    itemGap = 0,
    showSeparator = false,
    separatorWidth = 1,
    separatorColor = "#e0e0e0",
    
    // Debug
    debug = false,
    
    // Delete button visibility
    showDeleteButton = false,
    
    ...props
}) => {
    const {
        connectors: { connect, drag },
        isSelected,
        isHovered,
    } = useNode((state) => ({
        isSelected: state.events.selected,
        isHovered: state.events.hovered,
    }));

    const { enabled } = useEditor((state) => ({
        enabled: state.options.enabled
    }));

    // Session ID pour isoler les demandes de cette session
    const [sessionId] = useState(() => {
        try {
            const storageKey = `qwanyx-requests-session-${requestDomain}`;
            let sessionId = localStorage.getItem(storageKey);
            if (!sessionId) {
                sessionId = generateSessionId();
                localStorage.setItem(storageKey, sessionId);
            }
            return sessionId;
        } catch (error) {
            console.error('[Requests] Error with session storage:', error);
            return generateSessionId();
        }
    });

    // État utilisateur avec persistance localStorage
    const [userInfo, setUserInfo] = useState(() => {
        try {
            const savedUserInfo = localStorage.getItem(`qwanyx-requests-user-${requestDomain}`);
            return savedUserInfo ? JSON.parse(savedUserInfo) : {
                email: '',
                nom: '',
                telephone: '',
                adresse: ''
            };
        } catch (error) {
            console.error('[Requests] Error loading user info from localStorage:', error);
            return {
                email: '',
                nom: '',
                telephone: '',
                adresse: ''
            };
        }
    });
    
    const [userInfoValid, setUserInfoValid] = useState(false);
    const [isCreatingRequest, setIsCreatingRequest] = useState(false);
    const [lastCreatedRequest, setLastCreatedRequest] = useState(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentTab, setCurrentTab] = useState(0); // 0: Formulaire, 1: Demandes

    // Sauvegarde automatique des données utilisateur
    useEffect(() => {
        try {
            localStorage.setItem(`qwanyx-requests-user-${requestDomain}`, JSON.stringify(userInfo));
        } catch (error) {
            console.error('[Requests] Error saving user info to localStorage:', error);
        }
    }, [userInfo, requestDomain]);

    // Validation des informations utilisateur
    useEffect(() => {
        const isValid = userInfo.email && 
                       userInfo.email.trim() && 
                       /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email) &&
                       userInfo.nom && 
                       userInfo.nom.trim();
        setUserInfoValid(isValid);
    }, [userInfo]);

    // Force refresh when template changes
    useEffect(() => {
        setRefreshKey(prev => prev + 1);
    }, [requestTemplate]);

    // Fonction pour créer une nouvelle demande simple (comme Shop)
    const createRequest = async (requestData = {}) => {
        if (!userInfoValid) {
            alert('Veuillez remplir vos coordonnées avant de créer une demande.');
            return;
        }

        setIsCreatingRequest(true);
        
        try {
            // Créer un simple record avec les données au niveau racine (comme Shop)
            const newRequest = newQwanta(
                null, // editorId
                requestDomain, // frameId (di)
                null, // editorParent
                'request', // qwantaType
                {
                    // Données utilisateur pour filtrage
                    userEmail: userInfo.email,
                    userNom: userInfo.nom,
                    userTelephone: userInfo.telephone,
                    userAdresse: userInfo.adresse,
                    sessionId: sessionId, // Isolement par session
                    
                    // Données de la demande
                    title: requestData.title || 'Nouvelle demande',
                    description: requestData.description || '',
                    priority: requestData.priority || 'normale',
                    status: 'nouvelle',
                    
                    // Métadonnées
                    requestDate: new Date().toISOString(),
                    requestType: 'demande'
                }
            );

            const result = await persist(newRequest, newRequest._id, newRequest.di, newRequest.ed);
            
            if (debug) {
                console.log('[Requests] Demande créée avec succès:', result);
                console.log('[Requests] Session ID:', sessionId);
                console.log('[Requests] User Email:', userInfo.email);
                console.log('[Requests] Request Domain:', requestDomain);
            }

            setLastCreatedRequest(result);
            setRefreshKey(prev => prev + 1); // Force refresh du ViewSwitcher
            alert('Demande créée avec succès !');

        } catch (error) {
            console.error('[Requests] Erreur lors de la création de la demande:', error);
            alert('Erreur lors de la création de la demande. Veuillez réessayer.');
        } finally {
            setIsCreatingRequest(false);
        }
    };

    // Fonction pour supprimer une demande
    const deleteRequest = async (requestId) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
            return;
        }

        try {
            console.log('[Requests] Tentative de suppression de la demande:', requestId);
            const result = await deleteInstance(requestId);
            console.log('[Requests] Résultat de la suppression:', result);
            
            // Force refresh du ViewSwitcher de plusieurs façons
            setRefreshKey(prev => {
                const newKey = prev + 1;
                console.log('[Requests] Nouveau refresh key:', newKey);
                return newKey;
            });
            
            // Petit délai pour laisser le temps au serveur de traiter
            setTimeout(() => {
                setRefreshKey(prev => prev + 1);
                console.log('[Requests] Refresh supplémentaire après délai');
            }, 500);
            
            alert('Demande supprimée avec succès !');
            
        } catch (error) {
            console.error('[Requests] Erreur lors de la suppression de la demande:', error);
            alert('Erreur lors de la suppression de la demande: ' + error.message);
        }
    };

    // Gestionnaire de changement d'onglet
    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
    };

    // Contexte fourni aux templates
    const requestContextValue = {
        createRequest,
        deleteRequest,
        userInfo,
        userInfoValid,
        isCreatingRequest,
        lastCreatedRequest,
        sessionId,
        requestDomain
    };

    const containerStyle = {
        width,
        height,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        overflow: 'hidden',
        // Assurer que le container occupe bien toute la hauteur
        boxSizing: 'border-box',
        ...getSelectionStyle(isSelected, isHovered, enabled)
    };

    return (
        <RequestContext.Provider value={requestContextValue}>
            <div
                ref={ref => enabled ? connect(drag(ref)) : connect(ref)}
                style={containerStyle}
                {...props}
            >
                {/* Onglets */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs 
                        value={currentTab} 
                        onChange={handleTabChange} 
                        aria-label="Onglets des demandes"
                    >
                        <Tab 
                            icon={<FormIcon />} 
                            label="Formulaire" 
                            id="tab-0" 
                            aria-controls="tabpanel-0" 
                        />
                        <Tab 
                            icon={<RequestsIcon />} 
                            label="Demandes" 
                            id="tab-1" 
                            aria-controls="tabpanel-1"
                            disabled={!userInfoValid} // Désactivé si formulaire non validé
                        />
                    </Tabs>
                </Box>

                {/* Contenu des onglets */}
                <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    
                    {/* Onglet Formulaire */}
                    {currentTab === 0 && (
                        <Box sx={{ p: 2, overflow: 'auto' }}>
                            {requireUserInfo && (
                                <UserInfoForm
                                    userInfo={userInfo}
                                    onUserInfoChange={setUserInfo}
                                    isValid={userInfoValid}
                                    title={userFormTitle}
                                    emailRequired={emailRequired}
                                    showPhone={showPhone}
                                    showAddress={showAddress}
                                />
                            )}

                            {/* Message de validation */}
                            {userInfoValid && (
                                <Alert severity="success" sx={{ mt: 2 }}>
                                    Informations validées ! Vous pouvez maintenant accéder à l'onglet "Demandes" pour créer et gérer vos demandes.
                                </Alert>
                            )}
                        </Box>
                    )}

                    {/* Onglet Demandes */}
                    {currentTab === 1 && userInfoValid && (
                        <Box sx={{ 
                            flex: 1, 
                            overflow: 'hidden', 
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Bouton pour créer une nouvelle demande */}
                            <Box sx={{ p: 2, borderBottom: '1px solid #eee' }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={() => createRequest()}
                                    disabled={isCreatingRequest}
                                    fullWidth
                                >
                                    {isCreatingRequest ? 'Création...' : 'Créer une nouvelle demande'}
                                </Button>
                            </Box>

                            {/* Liste des demandes */}
                            <Box sx={{ 
                                flex: 1, 
                                overflow: 'hidden', 
                                display: 'flex',
                                flexDirection: 'column',
                                minHeight: 0,
                                backgroundColor: debug ? '#f0f0f0' : 'transparent'
                            }}>
                                {/* Info de debug */}
                                {debug && (
                                    <Box sx={{ p: 1, backgroundColor: '#f5f5f5', fontSize: '0.75rem', color: '#666' }}>
                                        Debug: Domain={requestDomain}, Session={sessionId?.substring(0, 10)}..., Email={userInfo.email}
                                        {requestTemplate ? `, Template=${requestTemplate}` : ', Template=default'}
                                    </Box>
                                )}
                                
                                <ViewSwitcher
                                    key={`${requestTemplate}-${refreshKey}`}
                                    // Configuration Stack standard
                                    rootId={null}
                                    di={requestDomain}
                                    defaultView="flat"
                                    onSelect={null}
                                    
                                    // UI Stack standard
                                    showSearch={showSearch}
                                    showViewSelector={showSearch}
                                    showFuzzyToggle={showSearch}
                                    showSearchInfo={showSearch}
                                    showLoadingState={showLoadingState}
                                    showFacets={showSearch}
                                    showFacetCounts={showSearch}
                                    searchPlaceholder={searchPlaceholder}
                                    
                                    // Filtrage par email et session
                                    searchFields={searchFields}
                                    facetFields={['userEmail', 'sessionId', ...facetFields]}
                                    facetConfig={[
                                        {
                                            field: 'userEmail',
                                            value: userInfo.email,
                                            autoApply: true,
                                            hidden: true
                                        },
                                        {
                                            field: 'sessionId',
                                            value: sessionId,
                                            autoApply: true,
                                            hidden: true
                                        },
                                        ...facetConfig
                                    ]}
                                    
                                    // Tri
                                    sortBy={sortBy}
                                    sortOrder={sortOrder === 'desc' ? -1 : 1}
                                    showSortControls={showSortControls}
                                    
                                    // Template Stack standard
                                    itemTemplate={requestTemplate}
                                    categoryTemplate={null}
                                    useCompact={false}
                                    templateProjection={[]}
                                    
                                    // Hauteur: container fixe, items de hauteur fixe
                                    itemHeight={itemHeight}
                                    fixedHeight={true}
                                    dynamicHeight={false}
                                    autoHeight={false}
                                    containerFixedHeight={true}
                                    
                                    // Props pour la vue plate
                                    flatViewProps={{
                                        itemHeight: itemHeight,
                                        containerHeight: "fixed",
                                        scrollable: true
                                    }}
                                    
                                    // Context
                                    extraContext={requestContextValue}
                                    
                                    // Debug
                                    debug={debug}
                                    
                                    // Delete button visibility
                                    showDeleteButton={showDeleteButton}
                                    
                                    // Style: container fixe, scroll pour débordement
                                    style={{ 
                                        flex: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        overflow: 'hidden',
                                        minHeight: 0
                                    }}
                                    
                                    // Container scroll - hauteur fixe
                                    containerStyle={{
                                        flex: 1,
                                        overflowY: 'auto',
                                        minHeight: 0,
                                        paddingBottom: '20px'
                                    }}
                                    
                                    // Force le respect de la hauteur container
                                    respectContainerHeight={true}
                                    scrollOnOverflow={true}
                                />
                            </Box>
                        </Box>
                    )}
                </Box>
            </div>
        </RequestContext.Provider>
    );
};

// Composant croix de suppression pour les items de liste
export const RequestDeleteCross = ({ 
    requestId,
    size = "small",
    confirmMessage = "Êtes-vous sûr de vouloir supprimer cette demande ?",
    style = {}
}) => {
    const { deleteRequest } = useRequestContext();

    const handleDelete = (e) => {
        e.stopPropagation(); // Empêcher la propagation du clic
        if (window.confirm(confirmMessage)) {
            deleteRequest(requestId);
        }
    };

    return (
        <IconButton
            onClick={handleDelete}
            size={size}
            color="error"
            sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 1)',
                },
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                ...style
            }}
        >
            <CloseIcon fontSize="small" />
        </IconButton>
    );
};

// Composant wrapper qui ajoute automatiquement la croix de suppression
export const RequestItemWrapper = ({ 
    requestId,
    children,
    showDeleteCross = true,
    confirmMessage = "Êtes-vous sûr de vouloir supprimer cette demande ?",
    style = {},
    ...props
}) => {
    return (
        <Box 
            sx={{ 
                position: 'relative', 
                width: '100%',
                ...style 
            }}
            {...props}
        >
            {children}
            {showDeleteCross && (
                <RequestDeleteCross 
                    requestId={requestId}
                    confirmMessage={confirmMessage}
                />
            )}
        </Box>
    );
};

// Composant bouton de suppression à utiliser dans les templates (conservé pour compatibilité)
export const RequestDeleteButton = ({ 
    requestId, 
    variant = "outlined",
    size = "small",
    color = "error",
    children = "Supprimer",
    showIcon = true,
    confirmMessage = "Êtes-vous sûr de vouloir supprimer cette demande ?"
}) => {
    const { deleteRequest } = useRequestContext();

    const handleDelete = () => {
        if (window.confirm(confirmMessage)) {
            deleteRequest(requestId);
        }
    };

    return (
        <Button
            variant={variant}
            size={size}
            color={color}
            startIcon={showIcon ? <DeleteIcon /> : null}
            onClick={handleDelete}
            sx={{ ml: 1 }}
        >
            {children}
        </Button>
    );
};

// Settings component for Requests - Simplifié comme Shop
const RequestsSettings = () => {
    const {
        actions: { setProp },
        name,
        requestDomain,
        requestTemplate,
        requireUserInfo,
        userFormTitle,
        emailRequired,
        showPhone,
        showAddress,
        showSearch,
        searchPlaceholder,
        sortBy,
        sortOrder,
        showSortControls,
        useCompact,
        width,
        height,
        itemHeight,
        debug,
        showDeleteButton
    } = useNode((node) => ({
        name: node.data.props.name,
        requestDomain: node.data.props.requestDomain,
        requestTemplate: node.data.props.requestTemplate,
        requireUserInfo: node.data.props.requireUserInfo,
        userFormTitle: node.data.props.userFormTitle,
        emailRequired: node.data.props.emailRequired,
        showPhone: node.data.props.showPhone,
        showAddress: node.data.props.showAddress,
        showSearch: node.data.props.showSearch,
        searchPlaceholder: node.data.props.searchPlaceholder,
        sortBy: node.data.props.sortBy,
        sortOrder: node.data.props.sortOrder,
        showSortControls: node.data.props.showSortControls,
        useCompact: node.data.props.useCompact,
        width: node.data.props.width,
        height: node.data.props.height,
        itemHeight: node.data.props.itemHeight,
        debug: node.data.props.debug,
        showDeleteButton: node.data.props.showDeleteButton
    }));

    return (
        <div>
            {/* Configuration de base */}
            <Box sx={{ mb: 2 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Configuration de base
                </FormLabel>
                
                <TextField
                    label="Nom du composant"
                    value={name || ''}
                    onChange={(e) => setProp(props => props.name = e.target.value)}
                    size="small"
                    fullWidth
                    margin="normal"
                />
                
                <TextField
                    label="Domaine des demandes"
                    value={requestDomain || ''}
                    onChange={(e) => setProp(props => props.requestDomain = e.target.value)}
                    size="small"
                    fullWidth
                    margin="normal"
                    helperText="Domaine où stocker les demandes (comme 'requests' ou 'support')"
                />
                
                <TextField
                    label="Template d'affichage (ID)"
                    value={requestTemplate || ''}
                    onChange={(e) => setProp(props => props.requestTemplate = e.target.value)}
                    size="small"
                    fullWidth
                    margin="normal"
                    helperText="Template unique pour afficher toutes les demandes dans la liste"
                />
            </Box>

            {/* Formulaire utilisateur */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Formulaire utilisateur
                </FormLabel>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={requireUserInfo || false}
                            onChange={(e) => setProp(props => props.requireUserInfo = e.target.checked)}
                        />
                    }
                    label="Afficher le formulaire utilisateur"
                />
                
                {requireUserInfo && (
                    <>
                        <TextField
                            label="Titre du formulaire"
                            value={userFormTitle || ''}
                            onChange={(e) => setProp(props => props.userFormTitle = e.target.value)}
                            size="small"
                            fullWidth
                            margin="normal"
                        />
                        
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showPhone || false}
                                    onChange={(e) => setProp(props => props.showPhone = e.target.checked)}
                                />
                            }
                            label="Afficher le champ téléphone"
                        />
                        
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showAddress || false}
                                    onChange={(e) => setProp(props => props.showAddress = e.target.checked)}
                                />
                            }
                            label="Afficher le champ adresse"
                        />
                    </>
                )}
            </Box>

            {/* Recherche */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Recherche
                </FormLabel>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={showSearch || false}
                            onChange={(e) => setProp(props => props.showSearch = e.target.checked)}
                        />
                    }
                    label="Afficher la recherche"
                />
                
                {showSearch && (
                    <TextField
                        label="Placeholder de recherche"
                        value={searchPlaceholder || ''}
                        onChange={(e) => setProp(props => props.searchPlaceholder = e.target.value)}
                        size="small"
                        fullWidth
                        margin="normal"
                    />
                )}
            </Box>

            {/* Tri */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Tri
                </FormLabel>
                
                <FormControl size="small" fullWidth margin="normal">
                    <Select
                        value={sortBy || 'requestDate'}
                        onChange={(e) => setProp(props => props.sortBy = e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="requestDate">Date de création</MenuItem>
                        <MenuItem value="title">Titre</MenuItem>
                        <MenuItem value="userNom">Nom utilisateur</MenuItem>
                        <MenuItem value="priority">Priorité</MenuItem>
                        <MenuItem value="status">Statut</MenuItem>
                        <MenuItem value="requestType">Type de demande</MenuItem>
                    </Select>
                </FormControl>
                
                <FormControl size="small" fullWidth margin="normal">
                    <Select
                        value={sortOrder || 'desc'}
                        onChange={(e) => setProp(props => props.sortOrder = e.target.value)}
                        displayEmpty
                    >
                        <MenuItem value="desc">Plus récent d'abord</MenuItem>
                        <MenuItem value="asc">Plus ancien d'abord</MenuItem>
                    </Select>
                </FormControl>
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={showSortControls || false}
                            onChange={(e) => setProp(props => props.showSortControls = e.target.checked)}
                        />
                    }
                    label="Afficher les contrôles de tri aux utilisateurs"
                />
            </Box>

            {/* Affichage */}
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Affichage
                </FormLabel>
                
                <TextField
                    label="Largeur"
                    value={width || ''}
                    onChange={(e) => setProp(props => props.width = e.target.value)}
                    size="small"
                    fullWidth
                    margin="normal"
                />
                
                <TextField
                    label="Hauteur"
                    value={height || ''}
                    onChange={(e) => setProp(props => props.height = e.target.value)}
                    size="small"
                    fullWidth
                    margin="normal"
                />
                
                <TextField
                    label="Hauteur des items (px)"
                    type="number"
                    value={itemHeight || 120}
                    onChange={(e) => setProp(props => props.itemHeight = parseInt(e.target.value) || 120)}
                    size="small"
                    fullWidth
                    margin="normal"
                    helperText="Hauteur fixe de chaque item de la liste en pixels"
                />
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={showDeleteButton || false}
                            onChange={(e) => setProp(props => props.showDeleteButton = e.target.checked)}
                        />
                    }
                    label="Show Delete Button (Cross)"
                />
                
                <FormControlLabel
                    control={
                        <Switch
                            checked={debug || false}
                            onChange={(e) => setProp(props => props.debug = e.target.checked)}
                        />
                    }
                    label="Mode debug"
                />
            </Box>
        </div>
    );
};

// Craft configuration pour la croix de suppression
RequestDeleteCross.craft = {
    props: {
        requestId: "",
        size: "small",
        confirmMessage: "Êtes-vous sûr de vouloir supprimer cette demande ?"
    },
    related: {
        settings: () => (
            <div>
                <TextField
                    label="ID de la demande"
                    value=""
                    size="small"
                    fullWidth
                    margin="normal"
                    helperText="Utilisez {_id} pour l'ID automatique"
                />
                <TextField
                    label="Message de confirmation"
                    value="Êtes-vous sûr de vouloir supprimer cette demande ?"
                    size="small"
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                />
            </div>
        )
    }
};

// Craft configuration pour le wrapper d'item
RequestItemWrapper.craft = {
    props: {
        requestId: "",
        showDeleteCross: true,
        confirmMessage: "Êtes-vous sûr de vouloir supprimer cette demande ?"
    },
    related: {
        settings: () => (
            <div>
                <TextField
                    label="ID de la demande"
                    value=""
                    size="small"
                    fullWidth
                    margin="normal"
                    helperText="Utilisez {_id} pour l'ID automatique"
                />
                <FormControlLabel
                    control={<Switch checked={true} />}
                    label="Afficher la croix de suppression"
                />
                <TextField
                    label="Message de confirmation"
                    value="Êtes-vous sûr de vouloir supprimer cette demande ?"
                    size="small"
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                />
            </div>
        )
    }
};

// Craft configuration pour le bouton de suppression
RequestDeleteButton.craft = {
    props: {
        requestId: "",
        variant: "outlined",
        size: "small", 
        color: "error",
        children: "Supprimer",
        showIcon: true,
        confirmMessage: "Êtes-vous sûr de vouloir supprimer cette demande ?"
    },
    related: {
        settings: () => (
            <div>
                <TextField
                    label="ID de la demande"
                    value=""
                    size="small"
                    fullWidth
                    margin="normal"
                    helperText="Utilisez {_id} pour l'ID automatique"
                />
                <TextField
                    label="Texte du bouton"
                    value="Supprimer"
                    size="small"
                    fullWidth
                    margin="normal"
                />
                <TextField
                    label="Message de confirmation"
                    value="Êtes-vous sûr de vouloir supprimer cette demande ?"
                    size="small"
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                />
            </div>
        )
    }
};

// Craft configuration - Simplifié comme Shop
Requests.craft = {
    props: {
        name: "Requests",
        requestDomain: "requests",
        requestTemplate: null, // Template unique pour l'affichage
        requireUserInfo: true,
        userFormTitle: "Vos coordonnées",
        emailRequired: true,
        showPhone: true,
        showAddress: true,
        showSearch: true,
        searchPlaceholder: "Rechercher dans mes demandes...",
        useCompact: false, // Désactivé pour hauteur auto
        width: "100%",
        height: "500px", // Réduire la hauteur par défaut
        itemHeight: 120, // Hauteur fixe des items par défaut
        itemGap: 0,
        showSeparator: false,
        separatorWidth: 1,
        separatorColor: "#e0e0e0",
        debug: false,
        showDeleteButton: false
    },
    related: {
        settings: RequestsSettings
    }
};